import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import moment from 'moment';
import { cloneDeep, set } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { markdownComponents, remarkPlugins } from '../utils/markdownConfig';
import { formatDateRangeString, formatIsoDatesInText, formatRuleDateRange } from '../../pages/calendar/utils/configDescriptions';
import { invalidatePricingAndCalendar } from '../../helpers/hooks';
import { Switch } from '../../components/Switch';

const API_BASE_URL = (process.env.REACT_APP_API_URL || "https://api.autumnplatform.com").replace(/\/$/, "");

type WidgetState = 'idle' | 'applying' | 'success' | 'error' | 'dismissed';

const SETTING_LABELS: Record<string, string> = {
  "aggressiveness": "Pricing aggressiveness",
  "competitive.influence": "Competitor influence",
  "competitive.positionOffset": "Price vs. competitors",
  "competitive.fadeWithUrgency": "Fade competitor effect near arrival",
  "competitive.closeInAmplifier": "Price to fill near arrival",
  "resistance.factor": "Rate change resistance",
  "events.responsiveness": "Event price sensitivity",
  "differentials.responsiveness": "Room type pricing sensitivity",
  "competitive.followCompetitors": "Match competitor prices",
  "competitive.minCompetitorGap": "Min gap vs. competitors",
  "competitive.maxCompetitorGap": "Max gap vs. competitors",
  "pickup.aggressiveness": "Booking pace aggressiveness",
  "pickup.emptyRoomThreshold": "Empty room trigger",
  "pickup.lastMinuteDropRate": "Last-minute discount rate",
  "pickup.highDemandBoostRate": "High-demand premium",
  "seasonal.baseMultiplier": "Season base multiplier",
  "seasonal.weekendMultiplier": "Weekend price boost",
  "seasonal.eventMultiplier": "Event price boost",
  "floorPrice": "Floor (min price)",
  "ceilingPrice": "Ceiling (max price)",
  "floor": "Floor (min price)",
  "ceiling": "Ceiling (max price)",
  "baseRate": "Base rate",
  "minRate": "Minimum rate",
  "maxRate": "Maximum rate",
  "dates": "Dates",
  "boost": "Price premium",
  "discount": "Discount",
  "multiplier": "Price multiplier",
  "actions": "Actions",
  "forecastedOccupancy": "Forecasted occupancy",
  "actualOccupancy": "Current occupancy",
  "occupancy": "Occupancy",
  "pickupPace": "Booking pace",
  "daysUntilArrival": "Days until arrival",
  "dayOfWeek": "Day of week",
  "adr": "Avg. daily rate",
  "revpar": "RevPAR",
  "competitorAvgRate": "Competitor avg. rate",
  "demandScore": "Demand score",
  "bookingVelocity": "Booking velocity",
  "roomsAvailable": "Rooms available",
  "roomsSold": "Rooms sold",
  "leadTime": "Lead time",
};


function humanizeSettingNames(md: string): string {
  let result = md;
  for (const [raw, friendly] of Object.entries(SETTING_LABELS)) {
    result = result.replaceAll(raw, friendly);
  }
  return result;
}

function extractDescription(md: string): string {
  const idx = md.search(/\n#{1,6}\s|\n\|/);
  return idx > 0 ? md.slice(0, idx).trim() : md.trim();
}

function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}

function formatDisplayValue(value: unknown, unit?: string): string {
  if (value === undefined || value === null) return "—";
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (typeof value === "string") {
    return value.replace(/(\d{1,3})(?=(\d{3})+(?!\d))/g, "$1,");
  }
  const formatted = typeof value === "number" ? formatNumber(value) : String(value);
  if (unit === "$") return `$${formatted}`;
  if (unit === "%") return `${formatted}%`;
  if (unit === "x") return `${formatted}x`;
  return formatted;
}

const PERCENT_KEYS = new Set([
  "competitive.positionOffset",
  "boost", "discount",
]);

const DOLLAR_KEYS = new Set([
  "floorPrice", "ceilingPrice", "floor", "ceiling",
  "baseRate", "minRate", "maxRate", "adr", "revpar",
]);

const MULTIPLIER_KEYS = new Set([
  "differentials.responsiveness",
  "events.responsiveness",
  "seasonal.baseMultiplier",
  "seasonal.weekendMultiplier",
  "seasonal.eventMultiplier",
  "multiplier",
]);

function inferUnit(key: string, explicitUnit?: string): string | undefined {
  if (explicitUnit) return explicitUnit;
  if (PERCENT_KEYS.has(key)) return "%";
  if (DOLLAR_KEYS.has(key)) return "$";
  if (MULTIPLIER_KEYS.has(key)) return "x";
  return undefined;
}

function InlineDatePicker({ selected, onChange, minDate, small = false }: {
  selected: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  small?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() =>
    selected ? moment(selected) : moment()
  );
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (dropRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const calWidth = 240;
      const centerLeft = rect.left + rect.width / 2 - calWidth / 2;
      setPos({ top: rect.bottom + 8, left: Math.max(4, centerLeft) });
    }
    setIsOpen(prev => !prev);
  };

  const days = useMemo(() => {
    const start = viewDate.clone().startOf("month").startOf("week");
    const end = viewDate.clone().endOf("month").endOf("week");
    const result: moment.Moment[] = [];
    const cur = start.clone();
    while (cur.isSameOrBefore(end, "day")) {
      result.push(cur.clone());
      cur.add(1, "day");
    }
    return result;
  }, [viewDate]);

  const isDisabled = useCallback((day: moment.Moment) => {
    if (!minDate) return false;
    return day.isBefore(moment(minDate), "day");
  }, [minDate]);

  const calendar = isOpen && createPortal(
    <div
      ref={dropRef}
      className="fixed z-[9999] bg-surface-primary border border-border-primary rounded-md shadow-lg p-2"
      style={{ top: pos.top, left: pos.left, width: 240 }}
    >
      {/* Arrow */}
      <div
        className="absolute -top-[6px] border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-border-primary"
        style={{ left: "50%", transform: "translateX(-50%)" }}
      />
      <div
        className="absolute -top-[5px] border-l-[5px] border-r-[5px] border-b-[5px] border-l-transparent border-r-transparent border-b-surface-primary"
        style={{ left: "50%", transform: "translateX(-50%)" }}
      />
      <div className="flex items-center justify-between mb-1.5">
        <button type="button" onClick={() => setViewDate(v => v.clone().subtract(1, "month"))} className="p-1 hover:bg-surface-secondary rounded">
          <ChevronLeft size={14} className="text-content-secondary" />
        </button>
        <span className="text-[12px] font-medium text-content-primary">
          {viewDate.format("MMMM YYYY")}
        </span>
        <button type="button" onClick={() => setViewDate(v => v.clone().add(1, "month"))} className="p-1 hover:bg-surface-secondary rounded">
          <ChevronRight size={14} className="text-content-secondary" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
          <div key={d} className="text-[10px] text-content-tertiary font-medium py-0.5">{d}</div>
        ))}
        {days.map(day => {
          const isCurrentMonth = day.month() === viewDate.month();
          const isSelected = selected && day.isSame(selected, "day");
          const isToday = day.isSame(moment(), "day");
          const disabled = isDisabled(day);
          return (
            <button
              key={day.format("YYYY-MM-DD")}
              type="button"
              disabled={disabled}
              onClick={() => { onChange(day.toDate()); setIsOpen(false); }}
              className={`text-[11px] py-1 rounded transition-colors
                ${!isCurrentMonth ? "text-content-tertiary/40" : ""}
                ${isSelected ? "bg-brand-primary text-white" : ""}
                ${!isSelected && isToday ? "border border-brand-primary text-brand-primary" : ""}
                ${!isSelected && isCurrentMonth && !disabled ? "text-content-primary" : ""}
                ${!isSelected && !disabled ? "hover:bg-gray-100 hover:rounded-md" : ""}
                ${disabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}
              `}
            >
              {day.date()}
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        className={`inline-flex items-center rounded-md border border-border-primary bg-surface-primary text-content-primary font-medium hover:bg-surface-secondary transition-colors cursor-pointer ${small ? "gap-1 px-1.5 py-0.5 text-[9px]" : "gap-1.5 px-2 py-1 text-[12px]"}`}
      >
        <Calendar size={small ? 8 : 10} className="text-content-tertiary" />
        {selected ? moment(selected).format(small ? "MMM D" : "MMM D, 'YY") : "Select date"}
      </button>
      {calendar}
    </>
  );
}

export interface EditableField {
  key: string;
  label: string;
  type: "number" | "boolean";
  bounds?: { min: number; max: number };
  step?: number;
  unit?: string;
  operator?: ">" | "<" | ">=" | "<=";
  payloadPath: string;
}

export interface DatePreset {
  label: string;
  startDate: string;
  endDate: string;
}

export interface SuggestionActionWidget {
  type: "suggestion-action";
  actionType:
    | "update-floor-ceiling"
    | "update-seasonal-settings"
    | "update-pricing-config"
    | "create-pricing-rule"
    | "update-pricing-rule"
    | "update-room-type-price-override"
    | "open-support-ticket"
    | "reset-pricing-config";
  title?: string;
  bodyMarkdown?: string;
  userActions?: ('approve' | 'adjust' | 'dismiss' | 'show-details')[];
  current: Record<string, unknown>;
  suggested: Record<string, unknown>;
  dateRange: string;
  apiCall: {
    endpoint: string;
    method: "POST" | "PUT" | "PATCH" | "DELETE";
    payload: Record<string, unknown>;
  };
  startDate?: string;
  endDate?: string;
  editableFields?: EditableField[];
  datePresets?: DatePreset[];
  datePayloadPaths?: { startDate: string; endDate: string };
  widgetId?: string;
}

interface SuggestionActionWidgetProps {
  widget: SuggestionActionWidget;
  onDismiss?: () => void;
  onApplySuccess?: () => void;
  onAdjust?: () => void;
  compact?: boolean;
  expanded?: boolean;
  initialState?: WidgetState;
  initialPersistedStatus?: 'applied' | 'adjusted' | 'dismissed';
  conversationId?: string;
  messageId?: string;
  partIndex?: number;
  onWidgetActive?: (key: string, info: { conversationId: string; messageId: string; actionType: string; widgetId?: string; partIndex?: number; useNewPersistence?: boolean }) => void;
  onWidgetInactive?: (key: string) => void;
  useNewPersistence?: boolean;
}

export function SuggestionActionWidget({ widget, onDismiss, onApplySuccess, compact = false, expanded = false, initialState = 'idle', initialPersistedStatus, conversationId, messageId, partIndex, onWidgetActive, onWidgetInactive, useNewPersistence = false }: SuggestionActionWidgetProps) {
  const queryClient = useQueryClient();
  const [widgetState, setWidgetState] = useState<WidgetState>(initialState);
  const [persistedStatus, setPersistedStatus] = useState<'applied' | 'adjusted' | 'dismissed' | null>(initialPersistedStatus ?? null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState(initialState === 'dismissed' || initialState === 'success');
  const canPersist = !!widget.widgetId || (!!messageId && partIndex != null);
  const actions = widget.userActions ?? ['approve', 'adjust', 'dismiss'];

  const [editedPayload, setEditedPayload] = useState<Record<string, unknown>>(() => cloneDeep(widget.apiCall.payload));
  const [editedStartDate, setEditedStartDate] = useState(widget.startDate);
  const [editedEndDate, setEditedEndDate] = useState(widget.endDate);
  const [editedSuggested, setEditedSuggested] = useState<Record<string, unknown>>(() => cloneDeep(widget.suggested));
  const [activeDateSource, setActiveDateSource] = useState<string>('suggested');

  const registrationKey = widget.widgetId ?? (messageId && partIndex != null ? `${messageId}:${partIndex}` : null);

  useEffect(() => {
    if (!registrationKey || initialState !== 'idle') return;
    onWidgetActive?.(registrationKey, {
      conversationId: conversationId ?? '',
      messageId: messageId ?? '',
      actionType: widget.actionType,
      widgetId: widget.widgetId,
      partIndex: partIndex ?? undefined,
      useNewPersistence,
    });
    return () => { onWidgetInactive?.(registrationKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistWidgetState = useCallback(async (status: 'applied' | 'adjusted' | 'dismissed', appliedPayload?: Record<string, unknown>) => {
    setPersistedStatus(status);
    if (!conversationId) { console.warn("[Widget] no conversationId, skipping persist"); return; }

    console.log("[Widget] persistWidgetState:", { status, useNewPersistence, widgetId: widget.widgetId, conversationId, messageId, partIndex });

    try {
      const { api } = await import('../../helpers/api');
      if (useNewPersistence && widget.widgetId) {
        console.log("[Widget] → new API (conversations.updateWidgetState)");
        await api.conversations.updateWidgetState(conversationId, widget.widgetId, {
          messageId: messageId ?? '',
          actionType: widget.actionType,
          status,
          ...(appliedPayload && { appliedPayload }),
        });
      } else if (messageId) {
        const legacyStatus = status === 'adjusted' ? 'applied' as const : status;
        console.log("[Widget] → old API (pricingConfigAgent.updateWidgetStatus)", { legacyStatus, widgetId: widget.widgetId });
        await api.pricingConfigAgent.updateWidgetStatus(conversationId, messageId, partIndex ?? 0, legacyStatus, widget.widgetId);
      } else {
        console.warn("[Widget] no messageId, skipping persist");
      }
      console.log("[Widget] persist success");
    } catch (err) {
      console.error("[Widget] persist failed:", err);
    }
  }, [useNewPersistence, widget.widgetId, widget.actionType, conversationId, messageId, partIndex]);

  const hasEditableDates = !!(widget.startDate && widget.endDate);
  const hasEditableFields = !!(widget.editableFields?.length);
  const isEditableIdle = widgetState === 'idle' && (hasEditableDates || hasEditableFields);

  const tableRows = useMemo(() => {
    const editableMap = new Map<string, EditableField>();
    widget.editableFields?.forEach(f => editableMap.set(f.key, f));

    return Object.keys(widget.suggested).map(key => {
      const field = editableMap.get(key);
      return {
        key,
        label: SETTING_LABELS[key] ?? field?.label ?? key,
        unit: inferUnit(key, field?.unit),
        editable: !!field,
        field: field ?? null,
        operator: field?.operator,
      };
    });
  }, [widget.suggested, widget.editableFields]);

  const hasOperatorColumn = tableRows.some(r => r.operator);

  const hasCurrent = Object.keys(widget.current).length > 0;
  const description = widget.bodyMarkdown
    ? formatIsoDatesInText(humanizeSettingNames(extractDescription(widget.bodyMarkdown)))
    : '';

  const handleDateChange = (which: "start" | "end", isoDate: string) => {
    if (which === "start") setEditedStartDate(isoDate);
    else setEditedEndDate(isoDate);

    if (widget.datePayloadPaths) {
      const path = which === "start" ? widget.datePayloadPaths.startDate : widget.datePayloadPaths.endDate;
      setEditedPayload(prev => {
        const next = cloneDeep(prev);
        set(next, path, isoDate);
        return next;
      });
    }
  };

  const handlePresetClick = (preset: DatePreset) => {
    handleDateChange("start", preset.startDate);
    handleDateChange("end", preset.endDate);
  };

  const handleFieldChange = (field: EditableField, value: number | boolean) => {
    setEditedSuggested(prev => ({ ...prev, [field.key]: value }));
    setEditedPayload(prev => {
      const next = cloneDeep(prev);
      set(next, field.payloadPath, value);
      return next;
    });
  };

  const handleApply = async () => {
    setWidgetState('applying');
    setErrorMessage('');
    if (registrationKey) onWidgetInactive?.(registrationKey);
    
    try {
      const authToken = localStorage.getItem('token');
      
      const fullEndpoint = widget.apiCall.endpoint.startsWith('http')
        ? widget.apiCall.endpoint
        : `${API_BASE_URL}${widget.apiCall.endpoint}`;
        
      const response = await fetch(fullEndpoint, {
        method: widget.apiCall.method,
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editedPayload),
      });
      
      if (response.ok) {
        setWidgetState('success');
        setIsCollapsed(true);
        invalidatePricingAndCalendar(queryClient);
        onApplySuccess?.();
        const isEdited = JSON.stringify(editedPayload) !== JSON.stringify(widget.apiCall.payload);
        persistWidgetState(isEdited ? 'adjusted' : 'applied', editedPayload);
      } else {
        // Handle different error scenarios
        let errorMsg = 'Failed to apply change';
        
        try {
          const error = await response.json();
          
          // Handle validation errors specifically
          if (response.status === 400 && error.category === 'VALIDATION' && Array.isArray(error.message)) {
            errorMsg = `Validation error: ${error.message.join(', ')}`;
          } else {
            errorMsg = error.message || errorMsg;
          }
        } catch (parseError) {
          // If response is not JSON (like HTML error page), provide context-aware message
          if (response.status === 404) {
            errorMsg = 'API endpoint not found';
          } else if (response.status >= 500) {
            errorMsg = 'Server error occurred';
          } else if (response.status === 401 || response.status === 403) {
            errorMsg = 'Authentication failed';
          } else if (response.status === 400) {
            errorMsg = 'Invalid request data';
          }
        }
        
        setErrorMessage(errorMsg);
        setWidgetState('error');
      }
    } catch (err) {
      console.error('Error applying suggestion:', err);
      
      // Provide more specific error messages
      let errorMsg = 'Failed to apply change';
      if (err instanceof TypeError && err.message.includes('fetch')) {
        errorMsg = 'Network error - check connection';
      }
      
      setErrorMessage(errorMsg);
      setWidgetState('error');
    }
  };

  const handleDismiss = () => {
    setWidgetState('dismissed');
    setIsCollapsed(true);
    onDismiss?.();
    persistWidgetState('dismissed');
    if (registrationKey) onWidgetInactive?.(registrationKey);
  };

  const headerSize = compact ? "text-[12px]" : "text-[13px]";
  const headerBadgeSize = compact ? "text-[11px]" : "text-[12px]";
  const textSize = compact ? "text-[11px]" : "text-[12px]";
  const pad = compact ? "px-3 py-2" : "px-4 py-3";

  const isDismissed = widgetState === 'dismissed';
  const isSuccess = widgetState === 'success';
  const statusLabel = isDismissed ? "Dismissed"
    : isSuccess ? (persistedStatus === 'adjusted' ? "Applied (adjusted)" : "Applied")
    : null;

  return (
    <div className={`suggestion-widget bg-surface-primary border border-border-primary rounded-lg shadow-sm overflow-hidden ${isDismissed ? "opacity-60" : ""}`}>
      {/* Header - always visible, clickable to toggle collapse */}
      <button
        type="button"
        onClick={() => setIsCollapsed(prev => !prev)}
        className={`${pad} w-full text-left ${isCollapsed ? "" : "border-b border-border-secondary"}`}
      >
        <div className="space-y-0.5">
          <div className="flex items-center justify-between gap-2">
            <h3 className={`${headerSize} font-semibold text-content-primary truncate flex-1 min-w-0`}>
              {widget.title ?? 'Pricing Suggestion'}
            </h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              {statusLabel ? (
                <span className={`${headerBadgeSize} ${isDismissed ? "text-content-tertiary" : "text-brand-primary"}`}>
                  {statusLabel}
                </span>
              ) : (
                <span className={`px-2 py-0.5 bg-brand-primary/10 text-brand-primary ${headerBadgeSize} rounded-md font-medium ${expanded ? "whitespace-nowrap" : "text-right"}`}>
                  {(() => {
                    const dateText = hasEditableDates && editedStartDate && editedEndDate
                      ? formatRuleDateRange(editedStartDate, editedEndDate)
                      : formatDateRangeString(widget.dateRange);
                    if (expanded || !dateText.includes(' – ')) return dateText;
                    const [start, end] = dateText.split(' – ');
                    return <>{start}<br />{end}</>;
                  })()}
                </span>
              )}
              <motion.div
                animate={{ rotate: isCollapsed ? 0 : 180 }}
                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              >
                <ChevronDown size={compact ? 12 : 14} className="text-content-tertiary" />
              </motion.div>
            </div>
          </div>
          {isSuccess && (
            <p className={`${textSize} text-content-tertiary`}>
              Pricing changes may take a few minutes to reflect.
            </p>
          )}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            key="widget-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            {/* Body */}
            <div className={`${pad} space-y-3`}>
              {/* Description text from bodyMarkdown (before table/headings) */}
              {description && (
                <div className={`prose max-w-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_*]:!text-[12px] break-words ${compact ? "[&_*]:!text-[10px]" : ""}`}>
                  <ReactMarkdown
                    components={markdownComponents}
                    remarkPlugins={remarkPlugins}
                  >
                    {description}
                  </ReactMarkdown>
                </div>
              )}

              {/* Structured table from current + suggested + editableFields */}
              {tableRows.length > 0 && (
                <table className="w-full table-fixed border-collapse border-spacing-0">
                  <thead className="bg-surface-secondary border-b border-dashed border-border-primary">
                    <tr>
                      <th className="text-left text-content-secondary text-[12px] font-medium py-2 px-3">Setting</th>
                      {hasCurrent && (
                        <th className="text-left text-content-secondary text-[12px] font-medium py-2 px-3">Current</th>
                      )}
                      {hasOperatorColumn && (
                        <th className="w-8" />
                      )}
                      <th className="text-left text-content-secondary text-[12px] font-medium py-2 px-3">Suggested</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dashed divide-border-secondary">
                    {tableRows.map(row => {
                      const suggestedVal = editedSuggested[row.key];
                      return (
                        <tr key={row.key} className="hover:bg-surface-secondary/50 transition-colors">
                          <td className="py-2 px-3 text-content-primary text-[12px]">{row.label}</td>
                          {hasCurrent && (
                            <td className="py-2 px-3 text-content-secondary text-[12px]">
                              {formatDisplayValue(widget.current[row.key], row.unit)}
                            </td>
                          )}
                          {hasOperatorColumn && (
                            <td className="py-2 px-1 text-content-primary text-[14px] text-center font-mono font-bold">
                              {row.operator ?? ''}
                            </td>
                          )}
                          <td className="py-2 px-3 text-content-primary text-[12px]">
                            {row.editable && row.field && isEditableIdle ? (
                              row.field.type === "boolean" ? (
                                <Switch
                                  checked={!!suggestedVal}
                                  onChange={(e) => handleFieldChange(row.field!, e.target.checked)}
                                />
                              ) : (
                                <div className="inline-flex items-center gap-1">
                                  {row.unit === "$" && (
                                    <span className="text-content-tertiary text-[12px]">$</span>
                                  )}
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={String(suggestedVal ?? '')}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      if (raw === '') { handleFieldChange(row.field!, 0); return; }
                                      const v = Number(raw);
                                      const max = row.field!.bounds?.max ?? (row.unit === "$" ? 9999 : 99);
                                      if (!isNaN(v) && v <= max) handleFieldChange(row.field!, v);
                                    }}
                                    min={row.field.bounds?.min}
                                    max={row.field.bounds?.max ?? (row.unit === "$" ? 9999 : 99)}
                                    step={row.field.step}
                                    className={`${row.unit === "$" ? "w-12" : "w-10"} px-1 py-0.5 border border-border-primary rounded bg-surface-primary text-content-primary text-[12px] text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                  />
                                  {row.unit && row.unit !== "$" && (
                                    <span className="text-content-tertiary text-[12px]">{row.unit}</span>
                                  )}
                                </div>
                              )
                            ) : (
                              formatDisplayValue(suggestedVal, row.unit)
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {hasEditableDates && (
                      <tr className="hover:bg-surface-secondary/50 transition-colors">
                        <td className="py-2 px-3 text-content-primary text-[12px]">Dates</td>
                        {hasCurrent && (
                          <td className="py-2 px-3 text-content-secondary text-[12px]">
                            {formatRuleDateRange(widget.startDate, widget.endDate)}
                          </td>
                        )}
                        {hasOperatorColumn && <td />}
                        <td className="py-2 px-3 text-content-primary text-[12px] !whitespace-normal">
                          {isEditableIdle ? (
                            <div className={`inline-flex gap-1.5 ${expanded ? "items-center flex-row" : "flex-col items-end"}`}>
                              <InlineDatePicker
                                selected={editedStartDate ? moment(editedStartDate).toDate() : null}
                                onChange={(date) => { handleDateChange("start", moment(date).format("YYYY-MM-DD")); setActiveDateSource(''); }}
                                small={!expanded && hasCurrent}
                              />
                              <div className="inline-flex items-center gap-1.5">
                                <span className={`text-content-tertiary ${!expanded && hasCurrent ? "text-[10px]" : "text-[12px]"}`}>–</span>
                                <InlineDatePicker
                                  selected={editedEndDate ? moment(editedEndDate).toDate() : null}
                                  onChange={(date) => { handleDateChange("end", moment(date).format("YYYY-MM-DD")); setActiveDateSource(''); }}
                                  minDate={editedStartDate ? moment(editedStartDate).toDate() : undefined}
                                  small={!expanded && hasCurrent}
                                />
                              </div>
                            </div>
                          ) : (
                            formatRuleDateRange(editedStartDate, editedEndDate)
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {/* Date preset pills */}
              {isEditableIdle && hasEditableDates && widget.datePresets && widget.datePresets.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      handleDateChange("start", widget.startDate!);
                      handleDateChange("end", widget.endDate!);
                      setActiveDateSource('suggested');
                    }}
                    className={`px-2 py-0.5 rounded-md text-caption-one font-medium transition-colors ${
                      activeDateSource === 'suggested'
                        ? "bg-brand-primary text-content-inverse"
                        : "bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20"
                    }`}
                  >
                    Suggested
                  </button>
                  {[...widget.datePresets].reverse().map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => {
                        handlePresetClick(preset);
                        setActiveDateSource(preset.label);
                      }}
                      className={`px-2 py-0.5 rounded-md text-caption-one font-medium transition-colors ${
                        activeDateSource === preset.label
                          ? "bg-brand-primary text-content-inverse"
                          : "bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20"
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer - Status dependent */}
            <div className={`${pad} border-t border-border-secondary`}>
              {widgetState === 'idle' && canPersist && (
                <div className="flex items-center gap-3">
                  {actions.includes('dismiss') && (
                    <button
                      onClick={handleDismiss}
                      className={`px-3 py-1.5 ${textSize} text-pink-primary hover:opacity-80 transition-colors`}
                    >
                      Dismiss
                    </button>
                  )}
                  <div className="ml-auto flex items-center gap-2">
                    {actions.includes('approve') && (
                      <button
                        onClick={handleApply}
                        className="d-btn d-btn-primary d-btn-sm"
                      >
                        Apply Change
                      </button>
                    )}
                  </div>
                </div>
              )}
              {widgetState === 'idle' && !canPersist && (
                <div className={`flex items-center justify-center ${textSize} text-content-tertiary`}>
                  <span>Read-only</span>
                </div>
              )}

              {widgetState === 'applying' && (
                <div className={`flex items-center justify-center gap-2 ${textSize} text-content-secondary`}>
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Applying change...</span>
                </div>
              )}

              {widgetState === 'success' && (
                <div className={`flex items-center justify-center gap-2 ${textSize} text-brand-primary`}>
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>Change applied successfully!</span>
                </div>
              )}

              {widgetState === 'dismissed' && (
                <div className={`flex items-center justify-center gap-2 ${textSize} text-content-tertiary`}>
                  <span>No changes made</span>
                </div>
              )}

              {widgetState === 'error' && (
                <div className="flex flex-col items-center gap-2">
                  <div className={`flex items-center gap-2 ${textSize} text-red-600`}>
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <span>{errorMessage}</span>
                  </div>
                  <button
                    onClick={() => setWidgetState('idle')}
                    className="px-3 py-1 text-caption-one text-brand-primary hover:text-brand-primary-hover transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}